/* eslint-disable react/prop-types */
/* eslint-disable max-len */
import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Row,
  Col,
  message,
  Divider,
} from 'antd';
import {
  formatAndNotificateError,
  formatError,
  formatString,
} from 'utils';
import {
  Button,
  Drawer,
  Slider,
} from 'components/gyramais';
import useWindowDimensions from 'utils/hooks/useWindowDimensions';

import './styles.less';
import { useMutation, useQuery } from '@apollo/client';
import {
  CALCULATE_LOAN_VOLTZ, LOAN, SAVE_LOAN, SAVE_LOAN_EVENT,
} from './graphql';

let debounce;

/**
 *
 * @param {{
 *  loanId: any
 *  visible: boolean
 *  onClose?: void
 *  refetch: void
 * }} props
 * @returns {React.FC}
 */
const PreApprovedVoltzDrawer = ({
  loanId,
  visible,
  onClose,
  refetch,
}) => {
  const { width } = useWindowDimensions();

  const [contratoVoltz, setContratoVoltz] = useState();
  const [installments, setInstallments] = useState(0);
  const [amount, setAmount] = useState(0);
  const [paymentDay, setPaymentDay] = useState(0);
  const [installmentAmount, setInstallmentAmount] = useState(0);

  const paymentDaysOptions = useMemo(() => [1, 5, 10, 15, 20]);

  const {
    data: {
      loan,
    } = {},
  } = useQuery(LOAN, {
    variables: {
      id: loanId,
    },
    skip: !loanId,
    onCompleted: ({ loan }) => {
      setInstallmentAmount(loan.installmentAmount || 0);
    },
  });

  const [calculateLoanVoltz] = useMutation(CALCULATE_LOAN_VOLTZ, {
    onCompleted: ({ calculateLoanVoltz: { installmentAmount } = {} }) => {
      setInstallmentAmount(installmentAmount);
    },
    onError: (e) => message.error(formatError(e)),
  });

  const [saveLoan, { loading: saveLoading }] = useMutation(SAVE_LOAN, {
    onError: (error) => formatAndNotificateError(error),
  });

  const [saveLoanEvent, { loading: saveLoanLoading }] = useMutation(SAVE_LOAN_EVENT, {
    onCompleted: async () => {
      onClose();
      await refetch();

    },
    onError: (error) => formatAndNotificateError(error),
  });

  const handleCalculate = ({ valor, prazo }) => {
    setInstallmentAmount(undefined);

    clearTimeout(debounce);

    debounce = setTimeout(() => {
      calculateLoanVoltz({
        variables: {
          loanId: loan.id,
          valor,
          prazo,
        },
      });
    }, 1000);
  };

  useEffect(() => {
    if (!loan?.id) {
      return;
    }

    const contract = loan.additionalInfo?.contratoVoltz;
    if (contract) {
      const parsed = JSON.parse(loan.additionalInfo.contratoVoltz);
      setAmount(parsed.valorDisponivel);
      setInstallments(parsed.prazo);
      setContratoVoltz(parsed);
    }

    const date = loan.firstPaymentDate && new Date(loan.firstPaymentDate);
    if (date) {
      date.setHours(date.getHours() + 3);

      const paymentDayIndex = paymentDaysOptions.indexOf(date.getDate());
      setPaymentDay(paymentDayIndex);
    }

    setInstallmentAmount(loan.installmentAmount);
  }, [loan?.id]);

  return (
    <Drawer
      title="Escolher valor pré-aprovado"
      visible={visible}
      onClose={onClose}
      onCancel={onClose}
      centered
      hideFooter
      width={800}
    >
      <Row gutter={[30, 16]} align="middle">
        <Col span={24}>
          <h5>
            <b>
              Prazo
            </b>
          </h5>

          <Slider
            tooltipVisible={false}
            min={6}
            max={contratoVoltz?.prazo || 36}
            step={1}
            showValues
            value={installments}
            valuesFormatter={(value) => `${value} meses`}
            onChange={(value) => {
              setInstallments(value);
              handleCalculate({ valor: amount, prazo: value });
            }}
          />
        </Col>

        <Col span={24}>
          <h5>
            <b>
              Valor solicitado
            </b>
          </h5>

          <Slider
            tooltipVisible={false}
            min={10000}
            max={contratoVoltz?.valorDisponivel || 10000}
            value={amount}
            onChange={(value) => {
              setAmount(value);
              handleCalculate({ valor: value, prazo: installments });
            }}
            step={1000}
            showValues
            valuesFormatter={(e) => {
              if (width <= 768) {
                return `${(formatString(e, 'currency'))?.split('.')[0]} mil`;
              }
              return formatString(e, 'currency');
            }}
            input={{ type: 'currency' }}
          />
        </Col>

        <Col span={24}>
          <h5>
            <b>
              Dia de vencimento
            </b>
          </h5>

          <Slider
            tooltipVisible={false}
            min={0}
            max={paymentDaysOptions.length - 1}
            marks={paymentDaysOptions}
            value={paymentDay}
            onChange={setPaymentDay}
          />
        </Col>

        <Col span={24}>
          <Divider style={{ margin: 0, padding: 0 }} />
        </Col>

        <Col xs={24} sm={24} md={12}>
          <span className="no-margin-bottom">
            <b style={{ color: 'var(--primary-button-color)' }}>
              Valor da parcela
            </b>
          </span>

          <h5 style={{ fontWeight: 'bolder' }}>
            {installmentAmount === undefined ? 'Calculando...' : formatString(installmentAmount, 'currency')}
          </h5>
        </Col>

        <Col sx={24} sm={24} md={12} align="right">
          <Button
            loading={saveLoading || saveLoanLoading}
            color="primary"
            onClick={async () => {
              await saveLoan({
                variables: {
                  id: loan.id,
                  paymentDay: Number(paymentDay),
                },
              });

              await saveLoanEvent({
                variables: {
                  loanId: loan.id,
                  amount,
                  statusValue: 'awaiting-signature',
                  installmentsCount: installments,
                },
              });
            }}
          >
            Escolher
          </Button>
        </Col>
      </Row>
    </Drawer>
  );
};

export default PreApprovedVoltzDrawer;
